/**
 * Server clock synchronization.
 *
 * Countdown deadlines (`phaseEndsAt`, `endsAt`) arrive as server-clock
 * timestamps. Rendering them against an unsynchronized device clock makes
 * every screen show a different remaining time. Realtime events carry a
 * `serverNow` timestamp; the measured offset converts server deadlines to
 * this device's clock. The offset absorbs one-way network latency, which is
 * negligible next to multi-second device clock skew.
 */

let serverClockOffsetMs = 0;

/** Record a `serverNow` value from a realtime event to update the offset. */
export function recordServerNow(serverNowIso: string | null | undefined): void {
  if (!serverNowIso) return;
  const serverNowMs = Date.parse(serverNowIso);
  if (Number.isNaN(serverNowMs)) return;
  serverClockOffsetMs = serverNowMs - Date.now();
}

/** Convert a server-clock ISO timestamp to an epoch ms on this device's clock. */
export function serverTimeToLocalMs(serverIso: string): number {
  return Date.parse(serverIso) - serverClockOffsetMs;
}

/** Update the clock offset from serverNow, then map the deadline onto this device's clock. */
export function toLocalPhaseEnd(
  endsAt: string | null | undefined,
  serverNow: string | null | undefined,
): number | null {
  recordServerNow(serverNow);
  return endsAt ? serverTimeToLocalMs(endsAt) : null;
}

/** Reset the measured offset. Intended for tests. */
export function resetServerClock(): void {
  serverClockOffsetMs = 0;
}
